import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ProgramSession {
  session_id: number
  session_name: string | null
  run_start: string
  run_end: string
  capacity: number
  order_num: number
  applied_count: number
}

interface ApplicationSetting {
  allow_member: number
  allow_guest: number
  require_name: number
  require_phone: number
  require_email: number
  require_affiliation: number
  require_grade: number
  consent_policy_id: number | null
}

interface ProgramInfo {
  program_id: number
  title: string | null
  has_sessions: boolean
  sessions: ProgramSession[]
  is_closed: number
  application_setting: ApplicationSetting | null
}

interface MemberInfo {
  name: string
  phone: string
  email: string
  affiliation_name: string
  grade: string
}

export default function ProgramApply() {
  const { program_id } = useParams<{ program_id: string }>()
  const navigate = useNavigate()

  const [program, setProgram] = useState<ProgramInfo | null>(null)
  const [member, setMember] = useState<MemberInfo | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const [guestName, setGuestName] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [guestAffiliation, setGuestAffiliation] = useState('')

  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null)

  const [infoChecked, setInfoChecked] = useState(false)
  const [consentChecked, setConsentChecked] = useState(false)

  const [alertModal, setAlertModal] = useState<string | null>(null)
  const [successModal, setSuccessModal] = useState(false)

  const [consentContent, setConsentContent] = useState('')
  const [consentTitle, setConsentTitle] = useState('')
  const [consentPolicyId, setConsentPolicyId] = useState<number | null>(null)

  useEffect(() => {
    fetchData()
  }, [program_id])

  const fetchData = async () => {
    setLoading(true)
    try {
      const res = await fetch(getApiUrl(`/marubit/api/program/${program_id}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setProgram(data)

      if (data.accepts_online_application === 0) {
        alert('온라인 신청을 받지 않는 프로그램입니다. 전화 또는 문자로 문의해주세요.')
        navigate(`/program/${program_id}`)
        return
      }

      // 회원 정보 확인
      const token = localStorage.getItem('access_token')
      let loggedIn = false

      if (token) {
        const memberRes = await fetch(getApiUrl('/marubit/api/member/me'), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (memberRes.ok) {
          const memberData = await memberRes.json()
          setMember({
            name: memberData.name ?? '',
            phone: memberData.phone ?? '',
            email: memberData.email ?? '',
            affiliation_name: memberData.affiliation_name ?? '',
            grade: memberData.grade ?? '',
          })
          setIsLoggedIn(true)
          loggedIn = true
        }
      }

      // 비로그인 + allow_guest=0 이면 로그인 페이지로
      if (!loggedIn && data.application_setting?.allow_guest === 0) {
        alert('로그인이 필요한 프로그램입니다.')
        navigate('/auth/login')
        return
      }

      // 동의서 불러오기
      const consentRes = await fetch(getApiUrl('/marubit/api/consent-policy/detail?consent_type=program'))
      if (consentRes.ok) {
        const consentData = await consentRes.json()
        setConsentTitle(consentData.title)
        setConsentContent(consentData.content)
        setConsentPolicyId(consentData.id ?? null)
      }

    } catch {
      alert('프로그램 정보를 불러오는 데 실패했습니다.')
      navigate('/program')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (program?.has_sessions && !selectedSessionId) {
      setAlertModal('회차를 선택해주세요.')
      return
    }
    if (!infoChecked) {
      setAlertModal('내 정보가 맞는지 확인 후 체크해주세요.')
      return
    }
    if (!consentChecked) {
      setAlertModal('개인정보 수집 및 이용에 동의해주세요.')
      return
    }

    setSubmitting(true)
    try {
      const token = localStorage.getItem('access_token')

      if (isLoggedIn && token) {
        // 회원 신청
        const res = await fetch(getApiUrl('/marubit/api/program/applications'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            program_id: Number(program_id),
            session_id: selectedSessionId,
            consent_policy_id: consentPolicyId,
            consent_agreed: 1,
          }),
        })
        if (!res.ok) {
          const err = await res.json()
          setAlertModal(err.detail ?? '신청에 실패했습니다.')
          return
        }
      } else {
        // 비회원 신청
        const setting = program?.application_setting
        if (setting?.require_name === 1 && !guestName.trim()) {
        setAlertModal('이름을 입력해주세요.'); return
        }
        if (setting?.require_phone === 1 && !guestPhone.trim()) {
        setAlertModal('전화번호를 입력해주세요.'); return
        }
        if (setting?.require_email === 1 && !guestEmail.trim()) {
        setAlertModal('이메일을 입력해주세요.'); return
        }
        if (setting?.require_affiliation === 1 && !guestAffiliation.trim()) {
        setAlertModal('소속을 입력해주세요.'); return
        }
        const res = await fetch(getApiUrl('/marubit/api/program/kiosk/guest/applications'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            program_id: Number(program_id),
            session_id: selectedSessionId,
            guest_name: guestName,
            guest_phone: guestPhone,
            guest_email: guestEmail || null,
            guest_affiliation: guestAffiliation || null,
            consent_policy_id: consentPolicyId,
            consent_agreed: 1,
          }),
        })
        if (!res.ok) {
          const err = await res.json()
          setAlertModal(err.detail ?? '신청에 실패했습니다.')
          return
        }
      }

      setSuccessModal(true)
    } catch {
      setAlertModal('신청 중 오류가 발생했습니다.')
    } finally {
      setSubmitting(false)
    }
  }

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center py-[14px] border-b border-gray-100">
      <span className="w-[100px] text-[13px] text-gray-400 font-medium shrink-0">{label}</span>
      <span className="text-[14px] text-[#222] font-medium">{value || '-'}</span>
    </div>
  )

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">프로그램 신청</h1>
          <p className="text-[14px] text-gray-400">{program?.title}</p>
        </div>

        {/* 회차 선택 */}
        {program?.has_sessions && program.sessions.length > 0 && (
          <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
              회차 선택 <span className="text-red-500 text-[13px]">(필수)</span>
            </h2>
            <div className="space-y-2">
              {program.sessions.map(session => {
                const isFull = session.capacity > 0 && session.applied_count >= session.capacity
                return (
                    <label
                    key={session.session_id}
                    className={[
                        'flex items-center gap-3 px-4 py-3 rounded-[10px] border transition-colors',
                        isFull
                        ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                        : selectedSessionId === session.session_id
                            ? 'border-[#887267] bg-[#f5f2f0] cursor-pointer'
                            : 'border-gray-200 hover:border-[#887267] cursor-pointer',
                    ].join(' ')}
                    >
                    <input
                        type="radio"
                        name="session"
                        value={session.session_id}
                        checked={selectedSessionId === session.session_id}
                        onChange={() => !isFull && setSelectedSessionId(session.session_id)}
                        disabled={isFull}
                        className="accent-[#887267]"
                    />
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                        <p className="text-[14px] font-semibold text-[#222]">
                            {session.session_name ?? `${session.order_num + 1}회차`}
                        </p>
                        {isFull && (
                            <span className="px-2 py-[2px] text-[11px] font-semibold bg-gray-200 text-gray-500 rounded-full">
                            마감
                            </span>
                        )}
                        </div>
                        <p className="text-[12px] text-gray-400">
                        {formatDateTime(session.run_start)} ~ {formatDateTime(session.run_end)}
                        {session.capacity > 0 && ` · ${session.applied_count}/${session.capacity}명`}
                        </p>
                    </div>
                    </label>
                )
                })}
            </div>
          </div>
        )}

        {/* 신청자 정보 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-1">
            신청자 정보
          </h2>

          {isLoggedIn && member ? (
            <>
              <p className="mt-0 mb-4 ml-[13px] text-[12px] text-gray-300">로그인한 계정의 정보입니다.</p>
              {program?.application_setting?.require_name === 1 && <InfoRow label="이름" value={member.name} />}
              {program?.application_setting?.require_phone === 1 && <InfoRow label="전화번호" value={member.phone} />}
              {program?.application_setting?.require_email === 1 && <InfoRow label="이메일" value={member.email} />}
              {program?.application_setting?.require_affiliation === 1 && <InfoRow label="소속명" value={member.affiliation_name} />}
              {program?.application_setting?.require_grade === 1 && <InfoRow label="학년" value={member.grade} />}
              <div className="flex items-center justify-between mt-5 px-4 py-[14px] bg-[#f5f2f0] rounded-[10px] border border-[#e8e0dc]">
                <label className="flex items-center gap-[10px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={infoChecked}
                    onChange={e => setInfoChecked(e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-[#887267]"
                  />
                  <span className="text-[13px] font-semibold text-[#887267]">내 정보가 맞습니다 <span className="text-red-500">(필수)</span></span>
                </label>
                <button
                  onClick={() => navigate('/mypage/edit')}
                  className="px-[14px] py-[6px] text-[12px] font-semibold text-[#887267] bg-white border-[1.5px] border-[#887267] rounded-[6px] cursor-pointer hover:bg-[#887267] hover:text-white transition-colors"
                >
                  내 정보 수정
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mt-0 mb-4 ml-[13px] text-[12px] text-gray-300">비회원으로 신청합니다.</p>
              <div className="space-y-3">
                {program?.application_setting?.require_name === 1 && (
                    <input
                    type="text"
                    placeholder="이름 *"
                    value={guestName}
                    onChange={e => setGuestName(e.target.value)}
                    className="w-full px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
                    />
                )}
                {program?.application_setting?.require_phone === 1 && (
                    <input
                    type="tel"
                    placeholder="전화번호 *"
                    value={guestPhone}
                    onChange={e => setGuestPhone(e.target.value)}
                    className="w-full px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
                    />
                )}
                {program?.application_setting?.require_email === 1 && (
                    <input
                    type="email"
                    placeholder="이메일 *"
                    value={guestEmail}
                    onChange={e => setGuestEmail(e.target.value)}
                    className="w-full px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
                    />
                )}
                {program?.application_setting?.require_affiliation === 1 && (
                    <input
                    type="text"
                    placeholder="소속 *"
                    value={guestAffiliation}
                    onChange={e => setGuestAffiliation(e.target.value)}
                    className="w-full px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
                    />
                )}
                </div>
              <div className="flex items-center gap-3 mt-4 px-4 py-[14px] bg-[#f5f2f0] rounded-[10px] border border-[#e8e0dc]">
                <input
                  type="checkbox"
                  checked={infoChecked}
                  onChange={e => setInfoChecked(e.target.checked)}
                  className="w-4 h-4 cursor-pointer accent-[#887267]"
                />
                <span className="text-[13px] font-semibold text-[#887267]">입력한 정보가 맞습니다 <span className="text-red-500">(필수)</span></span>
              </div>
              <div className="mt-3 text-center">
                <button
                  onClick={() => navigate('/auth/login')}
                  className="text-[13px] text-[#887267] underline"
                >
                  회원이신가요? 로그인하기
                </button>
              </div>
            </>
          )}
        </div>

        {/* 개인정보 동의 */}
        <div className="bg-white rounded-2xl p-7 mb-6 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
            {consentTitle || '개인정보 수집 및 이용 동의'}
          </h2>
          <div className="bg-[#fafafa] border border-gray-100 rounded-[10px] p-4 text-[13px] text-[#555] leading-relaxed mb-4 max-h-[120px] overflow-y-auto whitespace-pre-line">
            {consentContent || '불러오는 중...'}
          </div>
          <label className="flex items-center gap-[10px] cursor-pointer px-4 py-[14px] bg-[#f5f2f0] rounded-[10px] border border-[#e8e0dc]">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={e => setConsentChecked(e.target.checked)}
              className="w-4 h-4 cursor-pointer accent-[#887267]"
            />
            <span className="text-[13px] font-semibold text-[#887267]">
              개인정보 수집 및 이용에 동의합니다 <span className="text-red-500">(필수)</span>
            </span>
          </label>
        </div>

        {/* 신청 버튼 */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className={[
            'w-full h-[52px] text-white border-none rounded-xl text-[16px] font-bold transition-colors shadow-[0_4px_16px_rgba(136,114,103,0.25)]',
            submitting ? 'bg-[#b8a39b] cursor-not-allowed' : 'bg-[#887267] cursor-pointer hover:bg-[#6b5248]',
          ].join(' ')}
        >
          {submitting ? '신청 중...' : '신청하기'}
        </button>
      </div>

      {/* 경고 모달 */}
      {alertModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-2xl px-6 py-7 w-[300px] text-center shadow-[0_8px_32px_rgba(0,0,0,0.15)]">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="#ef4444" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <p className="text-[14px] text-[#444] mb-5 leading-relaxed">{alertModal}</p>
            <button
              onClick={() => setAlertModal(null)}
              className="w-full h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors"
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 성공 모달 */}
      {successModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-2xl px-6 py-7 w-[300px] text-center shadow-[0_8px_32px_rgba(0,0,0,0.15)]">
            <div className="w-12 h-12 rounded-full bg-[#f5f2f0] flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="#887267" strokeWidth={2}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <p className="text-[16px] font-bold text-[#887267] mb-2">신청 완료!</p>
            <p className="text-[14px] text-[#666] mb-5 leading-relaxed">
              프로그램 신청이 완료되었습니다.
            </p>
            <button
              onClick={() => {
                setSuccessModal(false)
                if (isLoggedIn) {
                  navigate('/program/check')
                } else {
                  navigate('/program/check', {
                    state: { guest_name: guestName, guest_phone: guestPhone },
                  })
                }
              }}
              className="w-full h-[42px] bg-[#887267] text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors"
            >
              신청 내역 확인
            </button>
          </div>
        </div>
      )}
    </div>
  )
}